import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import API_END_POINTS from '../../../core/constants/apiEndPoints';
import { AreasResponse } from '../../types/Area';
import {
  GroupAndRole,
  GroupRoleAssignment,
  InitiateOnboarding,
  InviteUser,
  Project,
  ProjectArea,
  ProjectData,
  ProjectNameDetails
} from '../../types/ProjectData';
import { DashboardKPI } from '../../types/Dashboard';

const {
  GET_ROLES,
  UPDATE_ACTIVE_DASHBOARD_LIST,
  INVITE_USER,
  UPDATE_DASHBOARDS_LIST,
  GET_PROJECT_BY_PROJECTID,
  GET_ALL_DASHBOARDS_LIST_BY_PROJECTID,
  FETCH_PROJECT_DETAILS,
  GET_AREAS,
  GET_ALL_PROJECTS,
  GET_ASSIGNED_PROJECT_NAMES,
  GET_NEW_AVAILABLE_PROJECTS,
  SEND_ONBOARDING_EMAIL,
  SAVE_PROJECT_DETAILS,
  GET_ALL_KPIS,
  UPDATE_ALL_KPIS,
  ASSIGNED_ROLES_TO_USER,
  PROJECT,
} = API_END_POINTS;
@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  private http = inject(HttpClient);

  projectData$ = new BehaviorSubject<ProjectData | null>(null);
  private _activeKPIs = new BehaviorSubject<string[]>([]);
  activeKPIs$ = this._activeKPIs.asObservable();

  setActiveUKPIs(urls: string[]) {
    this._activeKPIs.next(urls);
  }

  constructor() {}

  getProjectByProjectCode(projectCode: string): Observable<{
    data: { project: Project; groupRoles: GroupRoleAssignment[] };
  }> {
    return this.http.get<{
      data: { project: Project; groupRoles: GroupRoleAssignment[] };
    }>(GET_PROJECT_BY_PROJECTID(projectCode));
  }

  

  getProjectAreas = (projectId: string) => {
    return this.http.get<{
      data: ProjectArea[];
    }>(API_END_POINTS.FETCH_PROJECT_AREAS, { params: { projectId } });
  };

  setProjectData = (projectdata: ProjectData) => {
    this.projectData$.next(projectdata);
  };

  sendOnboardingEmail = (projectId: string, data: InitiateOnboarding) => {
    return this.http.post<string>(SEND_ONBOARDING_EMAIL(projectId), data);
  };

  getProjectDetails = (projectId: string) => {
    return this.http
      .get<{ data: ProjectData }>(
        API_END_POINTS.FETCH_PROJECT_DETAILS(projectId)
      )
      .pipe(
        tap({
          next: (projectdata) => {
            this.projectData$.next(projectdata.data);
          },
        })
      );
  };

  getCachedProjectData = () => {
    return this.projectData$.getValue();
  };
  getAssignedProjectNames(): Observable<{
    data: ProjectNameDetails[];
  }> {
    return this.http.get<{
      data: ProjectNameDetails[];
    }>(GET_ASSIGNED_PROJECT_NAMES);
  }

  getAllProjects(): Observable<{
    data: Project[];
  }> {
    return this.http.get<{
      data: Project[];
    }>(GET_ALL_PROJECTS);
  }

  getProjectNames(): Observable<{
    data: ProjectNameDetails[];
  }> {
    return this.http.get<{
      data: ProjectNameDetails[];
    }>(GET_NEW_AVAILABLE_PROJECTS);
  }

  getRoles(
    groupRoleAssignment: GroupRoleAssignment[]
  ): Observable<GroupAndRole[]> {
    return this.http.post<GroupAndRole[]>(GET_ROLES, {
      GroupRoleAssignment: groupRoleAssignment,
    });
  }

  inviteUser(
    data: InviteUser
  ): Observable<{ data: number; count: number; status: string }> {
    return this.http.post<{ data: number; count: number; status: string }>(
      INVITE_USER,
      { users: [data] }
    );
  }

  getAssignedRoleToUser(platformId: string): Observable<{
    default: { Id: number; Name: string };
    roles: { Id: number; Name: string }[];
  }> {
    return this.http.get<{
      default: { Id: number; Name: string };
      roles: { Id: number; Name: string }[];
    }>(ASSIGNED_ROLES_TO_USER(platformId));
  }

  getAreas(projectId: string): Observable<AreasResponse> {
    return this.http.get<AreasResponse>(GET_AREAS(projectId));
  }

  getAllDashboardsList(
    projectId: string
  ): Observable<{ data: DashboardKPI[] }> {
    return this.http.get<{ data: DashboardKPI[] }>(
      GET_ALL_DASHBOARDS_LIST_BY_PROJECTID(projectId)
    );
  }

  updateDashboardLinks(
    data: { dashboardId: string; link: string }[]
  ): Observable<{ data: { dashboardId: string; link: string }[] }> {
    return this.http.put<{ data: { dashboardId: string; link: string }[] }>(
      UPDATE_DASHBOARDS_LIST,
      data
    );
  }

  updateActiveDashboard(
    data: { dashboardId: string; projectId: string; isActive: boolean }[]
  ): Observable<{
    data: { dashboardId: string; projectId: string; isActive: boolean }[];
  }> {
    return this.http.put<{
      data: { dashboardId: string; projectId: string; isActive: boolean }[];
    }>(UPDATE_ACTIVE_DASHBOARD_LIST, data);
  }

  saveProjectData(data: ProjectData) {
    return this.http.put<{
      data: ProjectData;
    }>(SAVE_PROJECT_DETAILS, data);
  }

  getStoredProject(): Project {
    return JSON.parse(localStorage.getItem('projectDetails')!);
  }

  setProject(project: Project) {
    localStorage.setItem('projectDetails', JSON.stringify(project));
  }


  setProjectActiveFlag = (projectId: string, isActive: boolean) => {
    return this.http.put(PROJECT.ACTIVATE_PROJECT, { projectId, isActive });
  };
}
